import { Link } from "react-router-dom";
import { Mail, MapPin, Youtube, Facebook, Instagram, Twitter, Linkedin, Globe } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_590a4cbe-6d2f-42fa-9080-72b33b8524af/artifacts/218oq24l_TCM-Logo.PNG";

const footerLinks = {
  platform: [
    { name: "Marketplace", href: "/marketplace" },
    { name: "Advisory", href: "/advisory" },
    { name: "Emission Calculator", href: "/emission-calculator" },
  ],
  initiatives: [
    { name: "Bharat Climate Initiative", href: "/initiatives/bharat-climate" },
    { name: "GreenStay", href: "/programs/greenstay" },
    { name: "GreenTrack", href: "/programs/greentrack" },
    { name: "FlyGreen", href: "/programs/flygreen" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Insights", href: "/insights" },
    { name: "Contact", href: "/contact" },
    { name: "Work With Us", href: "/work-with-us" },
    { name: "Franchise", href: "/franchise" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Disclaimer", href: "/disclaimer" },
  ],
};

const socialLinks = [
  { 
    name: "YouTube", 
    icon: Youtube, 
    href: "https://youtube.com/@TheCarbonMeridian", 
    color: "bg-red-600 hover:bg-red-700",
    label: "@TheCarbonMeridian"
  },
  { 
    name: "Facebook", 
    icon: Facebook, 
    href: "https://www.facebook.com/profile.php?id=61584859585945", 
    color: "bg-blue-600 hover:bg-blue-700",
    label: "The Carbon Meridian"
  },
  { 
    name: "Instagram", 
    icon: Instagram, 
    href: "https://www.instagram.com/thecarbonmeridian/", 
    color: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500",
    label: "thecarbonmeridian"
  },
  { 
    name: "X (Twitter)", 
    icon: Twitter, 
    href: "https://x.com/carbonmeridian", 
    color: "bg-slate-900 hover:bg-black",
    label: "@carbonmeridian"
  },
];

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img src={LOGO_URL} alt="The Carbon Meridian" className="h-12 w-auto" />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              India's platform for carbon markets, climate participation and sustainability leadership.
            </p>
            <div className="space-y-3">
              <a
                href="mailto:netzero@thecarbonmeridian.com"
                className="flex items-center gap-3 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-5 w-5" />
                netzero@thecarbonmeridian.com
              </a>
              <a
                href="https://www.thecarbonmeridian.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
              >
                <Globe className="h-5 w-5" />
                www.thecarbonmeridian.com
              </a>
              <p className="flex items-center gap-3 text-sm text-slate-400">
                <MapPin className="h-5 w-5" />
                Chandigarh, India
              </p>
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="font-montserrat font-semibold text-white mb-4">Platform</h4>
            <ul className="space-y-2">
              {footerLinks.platform.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Initiatives Links */}
          <div>
            <h4 className="font-montserrat font-semibold text-white mb-4">Initiatives</h4>
            <ul className="space-y-2">
              {footerLinks.initiatives.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-montserrat font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-montserrat font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Media Section - Prominent */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="text-center mb-6">
            <h4 className="font-montserrat font-semibold text-white mb-2">Connect With Us</h4>
            <p className="text-sm text-slate-400">Follow us on social media for updates and insights</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-5 py-3 rounded-xl ${social.color} text-white transition-all hover:scale-105 shadow-lg`}
                aria-label={social.name}
                data-testid={`social-${social.name.toLowerCase().replace(/[^a-z]/g, '')}`}
              >
                <social.icon className="h-6 w-6" />
                <span className="text-sm font-medium hidden sm:inline">{social.label}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} The Carbon Meridian. All rights reserved.
            </p>
            <p className="text-sm text-slate-500">
              From Footprints to Futures 🌍
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
